import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, RefreshCcw, Home } from 'lucide-react';

type LegalPage = 'terms' | 'privacy' | 'refund';

interface LegalPagesProps {
  initialPage?: LegalPage;
  onBack?: () => void;
  showBackToHome?: boolean;
}

// Update these with your actual business details
const COMPANY_NAME = "TrackWithAI";
const COMPANY_EMAIL = "support@trackwith.ai";
const EFFECTIVE_DATE = "July 4, 2026";

export function LegalPages({ initialPage = 'terms', onBack, showBackToHome = false }: LegalPagesProps) {
  const [activePage, setActivePage] = useState<LegalPage>(initialPage);

  const pages = [
    { id: 'terms' as LegalPage, label: 'Terms of Service', icon: FileText },
    { id: 'privacy' as LegalPage, label: 'Privacy Policy', icon: Shield },
    { id: 'refund' as LegalPage, label: 'Refund Policy', icon: RefreshCcw },
  ];

  return (
    <div className="app-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-line">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {showBackToHome ? (
              <Link
                to="/"
                className="app-link mr-3 p-1 rounded flex items-center gap-1"
              >
                <Home className="w-5 h-5" />
                <span className="text-sm">Back to home</span>
              </Link>
            ) : onBack && (
              <button
                onClick={onBack}
                className="app-link mr-3 p-1 rounded"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-ink">Legal</h2>
          </div>
        </div>

        {/* Page Tabs */}
        <div className="flex space-x-1 mt-4">
          {pages.map((page) => (
            <button
              key={page.id}
              onClick={() => setActivePage(page.id)}
              className={`flex items-center px-3 py-2 rounded-pill text-sm font-medium transition-colors ${
                activePage === page.id
                  ? 'bg-brand-faint text-brand-ink border border-brand-soft'
                  : 'text-muted hover:bg-muted-soft border border-transparent'
              }`}
            >
              <page.icon className="w-4 h-4 mr-2" />
              {page.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 prose prose-sm max-w-none prose-headings:text-ink prose-p:text-ink-soft prose-li:text-ink-soft prose-strong:text-ink prose-a:text-brand-ink">
        {activePage === 'terms' && <TermsOfService />}
        {activePage === 'privacy' && <PrivacyPolicy />}
        {activePage === 'refund' && <RefundPolicy />}
      </div>
    </div>
  );
}

function TermsOfService() {
  return (
    <div>
      <h1>Terms of Service</h1>
      <p className="text-sm text-muted">Effective Date: {EFFECTIVE_DATE}</p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing or using {COMPANY_NAME} ("Service"), you agree to be bound by these Terms of Service ("Terms").
        If you do not agree to these Terms, do not use the Service.
      </p>

      <h2>2. Geographic Availability</h2>
      <p>
        <strong>The Service is currently only available to users located in the United States.</strong> By using the Service,
        you represent and warrant that you are located in the United States. We reserve the right to restrict access
        from other geographic locations. We plan to expand availability to additional countries in the future.
      </p>

      <h2>3. Description of Service</h2>
      <p>
        {COMPANY_NAME} is an AI-powered personal assistant that helps you track nutrition, workouts, and other personal data.
        The Service includes:
      </p>
      <ul>
        <li>Natural language chat interface for logging and querying data</li>
        <li>Data visualization and analytics</li>
        <li>Workspace storage for your personal data</li>
        <li>Web dashboard and mobile app access</li>
      </ul>

      <h2>4. Account Registration</h2>
      <p>
        To use the Service, you must create an account. You agree to:
      </p>
      <ul>
        <li>Provide accurate and complete registration information</li>
        <li>Maintain the security of your account credentials</li>
        <li>Promptly notify us of any unauthorized account access</li>
        <li>Accept responsibility for all activities under your account</li>
      </ul>

      <h2>5. Subscription and Billing</h2>
      <h3>5.1 Free Trial</h3>
      <p>
        New subscriptions begin with a 7-day free trial. A valid payment method is required
        to start the trial. If you do not cancel before the trial ends, your subscription
        automatically converts to a paid subscription and your payment method is charged the
        applicable monthly fee. If you cancel during the trial, you will not be charged, and
        access to the Service's coaching features ends when the trial ends.
      </p>

      <h3>5.2 Paid Subscriptions</h3>
      <p>
        Paid subscriptions ("Pro") provide ongoing access to the Service's coaching features
        and usage allowances. By subscribing, you agree to:
      </p>
      <ul>
        <li>Pay the applicable subscription fees</li>
        <li>Automatic renewal until cancelled</li>
        <li>Provide valid payment information</li>
      </ul>

      <h3>5.3 Billing</h3>
      <p>
        Subscriptions are billed monthly in advance. Payments are processed through Stripe.
        You authorize us to charge your payment method for recurring fees until you cancel.
      </p>

      <h2>6. Acceptable Use</h2>
      <p>You agree NOT to:</p>
      <ul>
        <li>Use the Service for any illegal purpose</li>
        <li>Attempt to gain unauthorized access to the Service or its systems</li>
        <li>Interfere with or disrupt the Service</li>
        <li>Upload malicious code or content</li>
        <li>Use the Service to store or transmit harmful, fraudulent, or deceptive content</li>
        <li>Resell or redistribute the Service without authorization</li>
        <li>Exceed rate limits or abuse the API</li>
        <li>Use automated systems to access the Service in a manner that exceeds reasonable use</li>
      </ul>

      <h2>7. Data and Privacy</h2>
      <p>
        Your use of the Service is also governed by our Privacy Policy. You retain ownership of your data.
        By using the Service, you grant us a license to process your data solely to provide the Service.
      </p>

      <h2>8. AI-Generated Content Disclaimer</h2>
      <p>
        The Service uses artificial intelligence to process requests and generate responses.
        AI-generated content:
      </p>
      <ul>
        <li>May contain errors or inaccuracies</li>
        <li>Should not be relied upon as medical, nutritional, or professional advice</li>
        <li>Is provided "as is" without warranties of accuracy</li>
      </ul>
      <p>
        <strong>Important:</strong> Always consult qualified professionals for medical, nutritional,
        or fitness advice. The Service is for informational and tracking purposes only.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        The Service, including its software, design, and content (excluding your data),
        is owned by {COMPANY_NAME} and protected by intellectual property laws.
        You may not copy, modify, or distribute any part of the Service without permission.
      </p>

      <h2>10. Termination</h2>
      <p>
        You may cancel your account at any time. We may suspend or terminate your account if you violate these Terms.
        Upon termination:
      </p>
      <ul>
        <li>Your right to use the Service ends immediately</li>
        <li>We may delete your data after a reasonable retention period</li>
        <li>You remain responsible for any outstanding charges</li>
      </ul>

      <h2>11. Disclaimer of Warranties</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
        EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </p>
      <p>
        We do not warrant that the Service will be uninterrupted, error-free, or secure.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
        WHETHER INCURRED DIRECTLY OR INDIRECTLY.
      </p>
      <p>
        OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS
        PRECEDING THE CLAIM.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless {COMPANY_NAME} from any claims, damages, or expenses
        arising from your use of the Service or violation of these Terms.
      </p>

      <h2>14. Changes to Terms</h2>
      <p>
        We may update these Terms from time to time. We will notify you of material changes via email
        or through the Service. Continued use after changes constitutes acceptance.
      </p>

      <h2>15. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict of law principles.
      </p>

      <h2>16. Contact</h2>
      <p>
        For questions about these Terms, contact us at: <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>
      </p>
    </div>
  );
}

function PrivacyPolicy() {
  return (
    <div>
      <h1>Privacy Policy</h1>
      <p className="text-sm text-muted">Effective Date: {EFFECTIVE_DATE}</p>

      <h2>1. Introduction</h2>
      <p>
        {COMPANY_NAME} ("we", "us", "our") respects your privacy. This Privacy Policy explains how we collect,
        use, disclose, and safeguard your information when you use our Service.
      </p>

      <h2>2. Information We Collect</h2>

      <h3>2.1 Information You Provide</h3>
      <ul>
        <li><strong>Account Information:</strong> Email address, phone number, name</li>
        <li><strong>Profile Data:</strong> Personal stats (height, weight, age, fitness goals) you choose to provide</li>
        <li><strong>User Content:</strong> Messages, nutrition logs, workout data, and other content you submit</li>
        <li><strong>Payment Information:</strong> Processed by Stripe; we do not store full payment card details</li>
      </ul>

      <h3>2.2 Information Collected Automatically</h3>
      <ul>
        <li><strong>Usage Data:</strong> Features used, message counts, timestamps</li>
        <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
        <li><strong>Cookies:</strong> Session cookies for authentication</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service</li>
        <li>Process your requests and transactions</li>
        <li>Send service-related communications</li>
        <li>Respond to your inquiries and support requests</li>
        <li>Monitor usage and enforce our Terms of Service</li>
        <li>Protect against fraud and unauthorized access</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>4. AI Processing</h2>
      <p>
        Your messages and data are processed by AI systems (including third-party AI providers like Anthropic)
        to provide the Service. This processing is necessary to:
      </p>
      <ul>
        <li>Understand and respond to your requests</li>
        <li>Parse nutrition and fitness information</li>
        <li>Generate visualizations and insights</li>
      </ul>
      <p>
        We use AI providers that maintain appropriate data protection standards. Your data is not used
        to train AI models without your explicit consent.
      </p>

      <h2>5. Data Sharing</h2>
      <p>We may share your information with:</p>

      <h3>5.1 Service Providers</h3>
      <ul>
        <li><strong>AWS:</strong> Cloud infrastructure and storage</li>
        <li><strong>Anthropic:</strong> AI processing</li>
        <li><strong>Stripe:</strong> Payment processing</li>
      </ul>

      <h3>5.2 Legal Requirements</h3>
      <p>We may disclose information if required by law or to protect our rights, safety, or property.</p>

      <h3>5.3 Business Transfers</h3>
      <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred.</p>

      <p><strong>We do not sell your personal information.</strong></p>

      <h2>6. Data Retention and Deletion</h2>
      <p>
        We retain your data for as long as your account is active. You may delete your
        account and associated data at any time from the Settings section of your dashboard;
        deletion requires typing a confirmation phrase and is permanent. You may also export
        your data from Settings before deleting. Alternatively, you may request deletion by
        contacting us at <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>; we will
        confirm by email when it is complete.
      </p>

      <h2>7. Data Security</h2>
      <p>We implement security measures including:</p>
      <ul>
        <li>Encryption in transit (TLS/HTTPS)</li>
        <li>Encryption at rest for stored data</li>
        <li>Access controls and authentication</li>
        <li>Regular security assessments</li>
      </ul>
      <p>
        However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security.
      </p>

      <h2>8. Your Rights</h2>
      <p>
        You may export your personal data or delete your account and associated data
        directly from the Settings section of your dashboard. You may also request access
        to, correction of, or deletion of your personal data by emailing{' '}
        <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>; we will confirm by email
        when your request is complete.
      </p>

      <h2>9. California Privacy Rights (CCPA)</h2>
      <p>California residents have additional rights including:</p>
      <ul>
        <li>Right to know what personal information is collected</li>
        <li>Right to delete personal information</li>
        <li>Right to opt-out of sale (we do not sell personal information)</li>
        <li>Right to non-discrimination for exercising privacy rights</li>
      </ul>

      <h2>10. International Data Transfers</h2>
      <p>
        Your data may be transferred to and processed in the United States. By using the Service,
        you consent to this transfer. We ensure appropriate safeguards are in place.
      </p>

      <h2>11. Children's Privacy</h2>
      <p>
        The Service is not intended for users under 18 years of age. We do not knowingly collect
        information from children. If we learn we have collected such information, we will delete it.
      </p>

      <h2>12. Cookies</h2>
      <p>We use essential cookies for:</p>
      <ul>
        <li>Authentication and session management</li>
        <li>Security and fraud prevention</li>
      </ul>
      <p>We do not use advertising or tracking cookies.</p>

      <h2>13. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy periodically. We will notify you of material changes
        via email or through the Service. The "Effective Date" at the top indicates the last revision.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        For privacy-related questions or to exercise your rights, contact us at:
      </p>
      <p>
        <strong>Email:</strong> <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>
      </p>
    </div>
  );
}

function RefundPolicy() {
  return (
    <div>
      <h1>Refund Policy</h1>
      <p className="text-sm text-muted">Effective Date: {EFFECTIVE_DATE}</p>

      <h2>1. Subscription Cancellation</h2>
      <p>
        You may cancel your subscription at any time through the Billing section of your dashboard
        or by contacting us at <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>.
      </p>
      <p>
        When you cancel:
      </p>
      <ul>
        <li>Your subscription remains active until the end of your current billing period</li>
        <li>You will not be charged for subsequent periods</li>
        <li>After the billing period ends, AI coaching access is paused until you resubscribe</li>
        <li>Your data is retained, and account export and deletion remain available at any time</li>
      </ul>

      <h2>2. Refund Eligibility</h2>

      <h3>2.1 Full Refund (Within 7 Days)</h3>
      <p>
        If you are not satisfied with your subscription, you may request a full refund within
        <strong> 7 days</strong> of your initial purchase or subscription renewal. To qualify:
      </p>
      <ul>
        <li>Request must be submitted within 7 days of the charge</li>
        <li>This is your first refund request, or it has been more than 12 months since your last refund</li>
      </ul>

      <h3>2.2 Pro-Rated Refunds</h3>
      <p>
        After 7 days, we generally do not offer refunds for partial months. However, we may consider
        pro-rated refunds in exceptional circumstances:
      </p>
      <ul>
        <li>Extended service outages (more than 24 hours)</li>
        <li>Critical bugs preventing core functionality</li>
        <li>Billing errors on our part</li>
      </ul>

      <h3>2.3 No Refunds</h3>
      <p>Refunds are not available for:</p>
      <ul>
        <li>Partial month usage after the 7-day period</li>
        <li>Accounts terminated for Terms of Service violations</li>
        <li>Dissatisfaction with AI-generated responses (as AI output varies)</li>
        <li>Failure to cancel before renewal (please cancel before your billing date)</li>
      </ul>

      <h2>3. How to Request a Refund</h2>
      <p>To request a refund:</p>
      <ol>
        <li>Email <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a> with subject "Refund Request"</li>
        <li>Include your account email and the reason for your request</li>
        <li>We will respond within 2 business days</li>
      </ol>

      <h2>4. Refund Processing</h2>
      <p>
        Approved refunds are processed through Stripe and typically appear on your statement within
        5-10 business days, depending on your financial institution.
      </p>

      <h2>5. Free Trial</h2>
      <p>
        New subscriptions include a 7-day free trial. No charge is made during the trial,
        so no refund applies to the trial period. If you cancel during the trial, you will
        not be charged and access ends when the trial ends. If the trial converts to a paid
        subscription, the refund terms above apply to that first charge.
      </p>

      <h2>6. Disputes</h2>
      <p>
        If you dispute a charge with your bank or credit card company before contacting us,
        we may suspend your account pending resolution. Please contact us first to resolve any billing issues.
      </p>

      <h2>7. Changes to This Policy</h2>
      <p>
        We may update this Refund Policy from time to time. Material changes will be communicated
        via email or through the Service.
      </p>

      <h2>8. Contact</h2>
      <p>
        For billing questions or refund requests, contact us at: <a href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>
      </p>
    </div>
  );
}

// Export individual components for potential standalone use
export { TermsOfService, PrivacyPolicy, RefundPolicy };
